// src/components/useSlides.js
//
// What the home page background and the product cards share about changing
// photos smoothly, whether a visitor pages through them or they play by
// themselves:
//  - Only the photo showing and the ones next to it are in the page. Those are
//    fetched once the photo showing has loaded, so they are decoded and ready
//    by the time they are wanted, and nothing is fetched at the moment of a
//    change.
//  - Playing moves on only onto a photo that is ready: on a slow connection the
//    current one stays a little longer instead of fading into nothing. The
//    countdown starts once the photo showing is ready, so each gets its time.
//  - The new photo fades in over the old one, which stays fully drawn
//    underneath until it is covered, so the picture never dips to the
//    background in between.
import { useCallback, useEffect, useRef, useState } from 'react';

const addAll = (set, values) =>
  values.every((value) => set.has(value)) ? set : new Set([...set, ...values]);

/** Whether the visitor has asked their device for less motion. */
export const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const pageHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

/**
 * Whether the element is out of sight — scrolled away (less than `threshold`
 * of it showing) or in a background tab — so anything playing in it can wait.
 */
export function useOutOfSight(ref, threshold = 0) {
  const [hidden, setHidden] = useState(pageHidden);
  const [offscreen, setOffscreen] = useState(false);

  useEffect(() => {
    const onChange = () => setHidden(pageHidden());
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setOffscreen(!entry.isIntersecting || entry.intersectionRatio < threshold),
      { threshold: [0, threshold] }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, threshold]);

  return hidden || offscreen;
}

/**
 * Which photo of a set is showing, and which are in the page.
 *
 * `keys` identify the photos; a different set starts again from the first.
 * `playing` and `seconds` drive the countdown; `firstDelayMs` is added to the
 * first one only, so several sets playing side by side do not all change at
 * once. `preload` is 'next' for a set that only ever moves forward, or 'both'
 * for one a visitor can also page back through.
 */
export function useSlides({ keys, playing = false, seconds = 6, firstDelayMs = 0, preload = 'next' }) {
  const count = keys.length;
  const signature = keys.join('|');

  const [index, setIndex] = useState(0);
  const [previous, setPrevious] = useState(null);
  const [mounted, setMounted] = useState(() => new Set([0]));
  const [ready, setReady] = useState(() => new Set());
  const [due, setDue] = useState(false);
  const firstCountdown = useRef(true);

  const shown = useRef(signature);
  useEffect(() => {
    if (shown.current === signature) return;
    shown.current = signature;
    firstCountdown.current = true;
    setIndex(0);
    setPrevious(null);
    setMounted(new Set([0]));
    setReady(new Set());
    setDue(false);
  }, [signature]);

  const next = count > 1 ? (index + 1) % count : index;
  const before = count > 1 ? (index - 1 + count) % count : index;
  const currentReady = ready.has(index);
  const running = playing && count > 1;

  // The photos next to the one showing, once it is ready.
  useEffect(() => {
    if (count > 1 && currentReady) {
      setMounted((current) => addAll(current, preload === 'both' ? [next, before] : [next]));
    }
  }, [count, currentReady, next, before, preload]);

  useEffect(() => {
    if (!running || !currentReady) return undefined;
    const delay = seconds * 1000 + (firstCountdown.current ? firstDelayMs : 0);
    const timer = setTimeout(() => setDue(true), delay);
    return () => clearTimeout(timer);
  }, [running, currentReady, index, seconds, firstDelayMs]);

  // Paused or out of sight starts the countdown again rather than jumping the
  // moment it resumes.
  useEffect(() => {
    if (!running) setDue(false);
  }, [running]);

  const go = useCallback(
    (target) => {
      if (count < 2) return;
      const to = ((target % count) + count) % count;
      if (to === index) return;
      firstCountdown.current = false;
      setPrevious(index);
      setIndex(to);
      setDue(false);
      setMounted((current) => addAll(current, [to]));
    },
    [count, index]
  );

  useEffect(() => {
    if (due && running && ready.has(next)) go(next);
  }, [due, running, ready, next, go]);

  const markReady = useCallback((i) => setReady((current) => addAll(current, [i])), []);

  /** The stacking and fade for photo `i`: on top, just under it, or hidden. */
  const layer = (i, fadeMs) => {
    if (i === index) return { zIndex: 2, opacity: 1, transition: `opacity ${fadeMs}ms ease` };
    if (i === previous) return { zIndex: 1, opacity: 1, transition: 'none' };
    return { zIndex: 0, opacity: 0, transition: 'none' };
  };

  return { index, mounted, go, markReady, layer };
}
